import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, cents, points } from "../../lib";
import { Loading } from "../../app/ui";
import { Err, PartnerPage, PRICE_COPY, SetupFooter, useAction } from "./common";
import type { RulesView, RulesState } from "./types";

// One row per rule: "Months 1–3 → 2 points per $1". The last row has no end ("Month 4+").
type Row = { to: string; rate: string };
type Form = {
  rows: Row[];
  capOn: boolean;
  cap: string;
  extraOn: boolean;
  extra: { from: string; to: string; who: "new" | "all"; rate: string };
};

const ym = (d: string) => d.slice(0, 7);

function fromView(v: RulesView | null, nextStart: string): Form {
  const start = ym(nextStart);
  if (!v)
    return {
      // A ladder by default, so the table explains itself (CHANGE-040): months 1–2, 3–5, then 6 on.
      rows: [{ to: "2", rate: "3" }, { to: "5", rate: "5" }, { to: "", rate: "10" }],
      capOn: false,
      cap: "",
      extraOn: false,
      extra: { from: start, to: start, who: "new", rate: "1" },
    };
  return {
    rows: v.rows.map((r) => ({
      to: r.to === null ? "" : String(r.to),
      rate: trim(r.rate),
    })),
    capOn: v.cap !== null,
    cap: v.cap === null ? "" : String(v.cap),
    extraOn: v.extra !== null,
    extra: v.extra
      ? {
          from: ym(v.extra.from),
          to: ym(v.extra.to),
          who: v.extra.who,
          rate: trim(v.extra.rate),
        }
      : { from: start, to: start, who: "new", rate: "1" },
  };
}

/** "2.0000" → "2", "0.5000" → "0.5" */
function trim(r: string): string {
  return r.includes(".") ? r.replace(/0+$/, "").replace(/\.$/, "") : r;
}

function toBody(f: Form) {
  return {
    rows: f.rows.map((r, i) => ({
      rate: r.rate,
      to: i === f.rows.length - 1 ? null : r.to,
    })),
    cap: f.capOn ? f.cap.trim() : null,
    extra: f.extraOn
      ? {
          from: f.extra.from,
          to: f.extra.to,
          who: f.extra.who,
          rate: f.extra.rate,
        }
      : null,
  };
}

/** Points on $1,000 at this rate, with BigInt so no float touches points: floor(1000 × rate). */
function onThousand(rate: string): number | null {
  const m = /^(\d+)(?:\.(\d{0,4}))?$/.exec(rate.trim());
  if (!m) return null;
  const e4 = BigInt(m[1]) * 10000n + BigInt((m[2] ?? "").padEnd(4, "0") || "0");
  return Number((e4 * 1000n) / 10000n);
}

/** $20 for whole dollars, $20.50 otherwise. */
function dollars(c: number): string {
  return c % 100 === 0 ? `$${(c / 100).toLocaleString("en-US")}` : cents(c);
}

function starts(f: Form, i: number): number {
  if (i === 0) return 1;
  const prev = Number.parseInt(f.rows[i - 1].to, 10);
  return Number.isNaN(prev) ? Number.NaN : prev + 1;
}

function costLine(f: Form): string {
  const first = onThousand(f.rows[0].rate);
  const lastIdx = f.rows.length - 1;
  const last = onThousand(f.rows[lastIdx].rate);
  if (first === null || last === null)
    return "Enter points per $1 for each rule.";
  if (lastIdx === 0 || first === last) {
    return `A customer spending $1,000 gets ${points(first)} points. You pay ${dollars(first)}.`;
  }
  const m = starts(f, lastIdx);
  return `A customer spending $1,000 gets ${points(first)} points in month 1 and ${points(last)} from month ${Number.isNaN(m) ? "…" : m}. You pay ${dollars(first)}, then ${dollars(last)}.`;
}

function monthName(d: string): string {
  return new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function RulesPage() {
  return (
    <PartnerPage>
      {(pid, role) => <RulesBody pid={pid} canEdit={role !== "viewer"} />}
    </PartnerPage>
  );
}

function RulesBody({ pid, canEdit }: { pid: string; canEdit: boolean }) {
  const q = useQuery({
    queryKey: ["partners", "rules", pid],
    queryFn: () => api<RulesState>("GET", `/api/partners/${pid}/rules`),
  });
  const [f, setF] = useState<Form | null>(null);
  const [saved, setSaved] = useState(false);
  const [base, setBase] = useState<Form | null>(null);
  useEffect(() => {
    if (q.data && f === null) {
      const v = fromView(q.data.upcoming ?? q.data.current, q.data.next_start);
      setF(v);
      setBase(v);
    }
  }, [q.data]);
  const save = useAction(
    () =>
      api<RulesState & { changed: boolean }>(
        "PUT",
        `/api/partners/${pid}/rules`,
        toBody(f!),
      ),
    (r) => {
      const v = fromView(r.upcoming ?? r.current, r.next_start);
      setF(v);
      setBase(v);
      setSaved(true);
    },
  );
  if (!q.data || !f) return <Loading />;
  const set = (patch: Partial<Form>) => {
    setSaved(false);
    setF({ ...f, ...patch });
  };
  const setRow = (i: number, patch: Partial<Row>) =>
    set({ rows: f.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const addRow = () => {
    const last = f.rows[f.rows.length - 1];
    const from = starts(f, f.rows.length - 1);
    const to = Number.isNaN(from) ? "" : String(from + 2);
    set({
      rows: [
        ...f.rows.slice(0, -1),
        { ...last, to },
        { to: "", rate: last.rate },
      ],
    });
  };
  const removeRow = (i: number) =>
    set({
      rows: f.rows
        .filter((_, j) => j !== i)
        .map((r, j, a) => (j === a.length - 1 ? { ...r, to: "" } : r)),
    });
  const upcoming = q.data.upcoming;

  const setFrom = (i: number, v: string) => {
    const n = Number.parseInt(v, 10);
    setRow(i - 1, { to: v.trim() === "" || Number.isNaN(n) ? "" : String(n - 1) });
  };
  const setExtra = (patch: Partial<Form["extra"]>) => set({ extra: { ...f.extra, ...patch } });

  return (
    <form
      className="page narrow stack"
      onSubmit={(e) => {
        e.preventDefault();
        save.run(undefined);
      }}
    >
      <h1>Points</h1>
      <p className="muted">{PRICE_COPY}</p>
      <section className="stack" data-testid="partners-rules">
        <table className="rules">
          <thead>
            <tr>
              <th>From month</th>
              <th>Points per $1</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {f.rows.map((r, i) => {
              const from = starts(f, i);
              return (
                <tr key={i} data-testid={`partners-rule-${i}`}>
                  <td>
                    {i === 0 ? (
                      <span className="num">1</span>
                    ) : (
                      <input
                        aria-label={`rule ${i + 1} from month`}
                        data-testid={`partners-rule-from-${i}`}
                        inputMode="numeric"
                        value={Number.isNaN(from) ? "" : String(from)}
                        disabled={!canEdit}
                        onChange={(e) => setFrom(i, e.target.value)}
                      />
                    )}
                  </td>
                  <td>
                    <input
                      aria-label={`rule ${i + 1} points per $1`}
                      data-testid={`partners-rule-rate-${i}`}
                      value={r.rate}
                      disabled={!canEdit}
                      onChange={(e) => setRow(i, { rate: e.target.value })}
                    />
                  </td>
                  <td className="right">
                    {canEdit && f.rows.length > 1 && (
                      <button
                        type="button"
                        className="remove"
                        aria-label={`Remove rule ${i + 1}`}
                        data-testid={`partners-rule-remove-${i}`}
                        onClick={() => removeRow(i)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {canEdit && (
          <button type="button" className="link-button" data-testid="partners-rule-add" onClick={addRow}>
            + Add
          </button>
        )}
        <p data-testid="partners-live-line">
          <strong>{costLine(f)}</strong>
        </p>
      </section>

      <section className="stack extras" data-testid="partners-rules-more">
        <div className="extra">
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              data-testid="partners-cap-on"
              checked={f.capOn}
              disabled={!canEdit}
              onChange={(e) => set({ capOn: e.target.checked, cap: f.cap || "10000" })}
            />
            <span className="track" />
            Limit points per customer
          </label>
          {f.capOn && (
            <label>
              Most points per customer per month
              <input
                data-testid="partners-cap"
                inputMode="numeric"
                value={f.cap}
                disabled={!canEdit}
                onChange={(e) => set({ cap: e.target.value })}
              />
            </label>
          )}
        </div>
        <div className="extra">
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              data-testid="partners-extra-on"
              checked={f.extraOn}
              disabled={!canEdit}
              onChange={(e) => set({ extraOn: e.target.checked })}
            />
            <span className="track" />
            Extra points for a while
          </label>
          {f.extraOn && (
            <div className="fields">
              <label>
                Extra points per $1
                <input
                  data-testid="partners-extra-rate"
                 
                  value={f.extra.rate}
                  disabled={!canEdit}
                  onChange={(e) => setExtra({ rate: e.target.value })}
                />
              </label>
              <label>
                Who gets them
                <select
                  data-testid="partners-extra-who"
                  value={f.extra.who}
                  disabled={!canEdit}
                  onChange={(e) => setExtra({ who: e.target.value as "new" | "all" })}
                >
                  <option value="new">New customers</option>
                  <option value="all">Everyone</option>
                </select>
              </label>
              <label>
                From
                <input
                  type="month"
                  data-testid="partners-extra-from"
                  value={f.extra.from}
                  disabled={!canEdit}
                  onChange={(e) => setExtra({ from: e.target.value })}
                />
              </label>
              <label>
                To
                <input
                  type="month"
                  data-testid="partners-extra-to"
                  value={f.extra.to}
                  disabled={!canEdit}
                  onChange={(e) => setExtra({ to: e.target.value })}
                />
              </label>
            </div>
          )}
        </div>
      </section>

      <Err error={save.error} />
      {canEdit && (
        <div className="row">
          <button
            className="primary"
            data-testid="partners-rules-save"
            disabled={save.busy}
          >
            Save
          </button>
          {saved && (
            <span data-testid="partners-rules-saved">
              Saved.{upcoming && ` Starts ${monthName(upcoming.starts)}.`}
            </span>
          )}
        </div>
      )}
      {!saved && upcoming && q.data.current && (
        <p className="muted" data-testid="partners-rules-upcoming">
          These start {monthName(upcoming.starts)}. Until then your current
          points apply.
        </p>
      )}
      <SetupFooter pid={pid} step="policy" disabled={save.busy || (canEdit && JSON.stringify(f) !== JSON.stringify(base))} />
    </form>
  );
}
